import {
  BaSeBreakpointContext,
  BaSeDialogContext,
  BaSeParagraph,
  BaSeShapeButton,
  BaSeSmall1,
  BaSeTag,
  BaSeTextArea,
  BaSeTheme,
  useDebounce,
} from '@base/react';
import React from 'react';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import Lista from './components/lista';
import { MicrophoneInformation } from './microphone-Information';
import {
  AnswerWrapper,
  Microphone,
  WrapperSupportMessage,
} from './selection-interview-styled';

const questionMap = {
  1: '1 - Descreva uma situação em que você fez alguma coisa por si mesmo',
  2: '2 - Descreva uma situação em que você teve que conseguir que alguém fizesse algo que você queria',
  3: '3 - Uma situação em que você teve muita dificuldade de conseguir algo',
  4: '4 - Descreva uma situação em que você ficou satisfeito por ter realizado algo',
  5: '5 - Conte-me uma situação em que você correu um risco',
};

export const StepContent = ({
  question,
  answer,
  isStepChangeInvalid,
  setIsStepChangeInvalid,
  validAnswer,
  onAnswerChange,
  saveAnswer,
  setIsAudioListening,
  validateCompleteAnswer,
  lengthStep,
  salved,
  setSalved,
  wordCounter,
  setPermissionToValidateFilter,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const breakpointController = React.useContext(BaSeBreakpointContext);
  const [answerText, setAnswerText] = React.useState(answer?.answer);
  const answerDebounce = useDebounce(answer, 2000);
  const [isEditing, setEditing] = React.useState(false);
  const [showTooltipState, setShowTooltipState] = React.useState(false);

  const { showModal } = React.useContext(BaSeDialogContext);

  const lengthListening = React.useMemo(() => {
    return wordCounter(answerText);
  }, [answerText, wordCounter]);

  const lengthDefault = React.useMemo(() => {
    return listening ? lengthListening : lengthStep;
  }, [lengthListening, lengthStep, listening]);

  const validLengthStep = React.useMemo(
    () => lengthDefault >= 550 || lengthDefault === 600,
    [lengthDefault],
  );

  const textError = React.useMemo(() => {
    if (lengthDefault < 120) {
      return 'Você precisa preencher no mínimo 120 palavras para continuar.';
    }

    if (lengthDefault === 600) {
      return 'Você atingiu o número máximo de 600 palavras.';
    }

    if (lengthDefault >= 550) {
      return 'Atenção. Você está quase atingindo o número máximo de 600 palavras permitidas para a resposta.';
    }

    return '';
  }, [lengthDefault]);

  const startRecordConfig = React.useMemo(() => {
    return {
      title: 'Atenção',
      titleColor: BaSeTheme.colors.institucionais.cinzaSebrae10,
      actions: [
        {
          action: 'Ok',
          type: 'primary',
          color: BaSeTheme.colors.institucionais.azulSebrae10,
        },
      ],
      dismissible: true,
      content: () => (
        <div>
          <startRecordContent>
            <BaSeParagraph>
              Estamos iniciando o recurso de voz, que será convertido em texto.
              <br />
              <br />
              Tenha atenção com a conversão, pois ela não é 100%.
              <br />
              <br />
              Ao finalizar, revise seu texto e veja se está com as devidas
              pontuações ao final de cada frase e parágrafo.
              <br />
              <br />
            </BaSeParagraph>
          </startRecordContent>
        </div>
      ),
      isFluid: false,
      onChooseAction: (action) => {
        if (action === 'Ok') {
          setEditing(true);
          setSalved(false);
          SpeechRecognition.startListening({
            continuous: true,
            language: 'pt-br',
          });
          resetTranscript();
        }
      },
    };
  }, [resetTranscript, setSalved]);

  const stopRecordConfig = React.useMemo(() => {
    return {
      title: 'Atenção',
      titleColor: BaSeTheme.colors.institucionais.cinzaSebrae10,
      actions: [
        {
          action: 'Ok',
          type: 'primary',
          color: BaSeTheme.colors.institucionais.azulSebrae10,
        },
      ],
      dismissible: true,
      content: () => (
        <div>
          <startRecordContent>
            <BaSeParagraph>
              Estamos finalizando o recurso de voz.
              <br />
              <br />
              Revise seu texto e veja se está com as devidas pontuações ao final
              de cada frase e parágrafo.
              <br />
              <br />
              Caso o texto não esteja devidamente escrito e pontuado, a
              avaliação poderá não ser bem sucedida.
              <br />
              <br />
            </BaSeParagraph>
          </startRecordContent>
        </div>
      ),
      isFluid: false,
    };
  }, []);

  const actionListeningMap = React.useMemo(
    () => ({
      true: () => {
        SpeechRecognition.stopListening();
        onAnswerChange({
          ...answer,
          answer: answerText,
        });
        resetTranscript();
        showModal(stopRecordConfig);
      },
      false: () => {
        showModal(startRecordConfig);
      },
    }),
    [
      showModal,
      stopRecordConfig,
      onAnswerChange,
      answer,
      answerText,
      resetTranscript,
      startRecordConfig,
    ],
  );

  const callSave = React.useCallback(() => {
    setEditing(false);
    saveAnswer(answerDebounce, setSalved);
  }, [answerDebounce, saveAnswer, setSalved]);

  React.useEffect(() => {
    if (listening && lengthListening === 600) {
      return actionListeningMap[true];
    }
    listening && setAnswerText((answer?.answer ?? '').concat(transcript));
    setIsAudioListening(listening);
  }, [
    actionListeningMap,
    answer,
    lengthListening,
    listening,
    setIsAudioListening,
    transcript,
  ]);

  React.useEffect(() => {
    answerDebounce?.answer && callSave();
  }, [answerDebounce, callSave, saveAnswer]);

  React.useEffect(() => {
    validateCompleteAnswer(answer);
    setSalved(false);
  }, [answer, setSalved, validateCompleteAnswer]);

  const openDialogMessage = React.useCallback(() => {
    return showModal({
      actions: [],
      size: 'small',
      title: 'Roteiro de ajuda',
      dismissible: true,
      dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
      dismissibleIconType: 'tertiary',
      content: ({ closeModal, emitEvent }) => (
        <div>
          <Lista question={question} />
        </div>
      ),
      onChooseAction: (action) => {},
    });
  }, [question, showModal]);

  return (
    <React.Fragment>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <BaSeSmall1
          isBold
          color={BaSeTheme.colors.institucionais.cinzaSebrae10}
        >
          {questionMap[question]}
        </BaSeSmall1>
        <BaSeShapeButton
          buttonType="button"
          nameIcon="question-circle"
          onClick={() => openDialogMessage()}
          shape="square"
          size="medium"
          sizeIcon="medium"
          type="tertiary"
        />
      </div>
      <WrapperSupportMessage>
        <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
          (Você pode digitar ou gravar um áudio clicando no microfone
          {breakpointController.inDesktop
            ? ' ao lado)'
            : ' do seu teclado, se estiver disponível em seu dispositivo)'}
        </BaSeSmall1>
        {(isEditing || listening) && (
          <BaSeTag
            color={BaSeTheme.colors.institucionais.cinzaSebrae45}
            icon={listening ? 'record-audio-solid' : 'refresh-alt'}
            label={listening ? 'Gravando...' : 'Salvando...'}
            limitedWidth={200}
            overrideColor={{
              iconColor: BaSeTheme.colors.institucionais.cinzaSebrae45,
            }}
            size="small"
            themeMode="dark"
            type="primary"
          />
        )}
        {salved && (
          <BaSeTag
            color={BaSeTheme.colors.institucionais.cinzaSebrae45}
            icon="cloud-upload"
            label="Resposta salva!"
            limitedWidth={200}
            overrideColor={{
              iconColor: BaSeTheme.colors.institucionais.cinzaSebrae45,
            }}
            size="small"
            themeMode="dark"
            type="primary"
          />
        )}
      </WrapperSupportMessage>
      <AnswerWrapper>
        <BaSeTextArea
          hasCounter="words"
          placeholder='Lembre-se de iniciar o texto de sua resposta utilizando o pronome "Eu".'
          onChange={(value) => {
            setEditing(true);
            setPermissionToValidateFilter(true);
            setSalved(false);
            setAnswerText(value);
            resetTranscript();
            setIsStepChangeInvalid(false);
            return onAnswerChange({
              ...answer,
              answer: value,
            });
          }}
          rows={20}
          textError={textError}
          textSize="medium"
          inputStatus={
            (isStepChangeInvalid && !validAnswer(answer)) || validLengthStep
              ? 'invalid'
              : 'valid'
          }
          value={listening ? answerText : answer?.answer}
          counterMinLength={120}
          counterMaxLength={600}
        />
        {browserSupportsSpeechRecognition && breakpointController.inDesktop && (
          <React.Fragment>
            <Microphone
              onMouseOver={() => setShowTooltipState((prev) => !prev)}
              onMouseLeave={() => setShowTooltipState((prev) => !prev)}
              onClick={actionListeningMap[listening]}
              recording={listening}
            />
            {showTooltipState && (
              <MicrophoneInformation
                inDesktop={true}
                answer={answer}
                showTooltip={true}
              />
            )}
            <MicrophoneInformation
              inDesktop={true}
              answer={answer}
              showTooltip={false}
            />
          </React.Fragment>
        )}
        {!breakpointController.inDesktop && (
          <div>
            <MicrophoneInformation
              inDesktop={false}
              answer={answer}
              showTooltip={false}
            />
            <Microphone
              onMouseOver={() => setShowTooltipState((prev) => !prev)}
              onMouseLeave={() => setShowTooltipState((prev) => !prev)}
              onClick={actionListeningMap[listening]}
              recording={listening}
            />
          </div>
        )}
      </AnswerWrapper>
    </React.Fragment>
  );
};
